import { EventListItem } from '@domain/event';
import { EventView } from '@hooks/use-event-navigation/use-event-navigation';
import { useGenericInfiniteQuery } from '@hooks/use-generic-infinite-query';
import { useGenericQuery } from '@hooks/use-generic-query';
import { Params, paramsSerializer } from '@utils/format-params';
import { request } from '@utils/request/request';
import { RequestMethod } from '@utils/request/request.type';

export interface EventListParams {
  search?: string;
  offset?: number;
  limit?: number;
  orderBy?: string;
  sort?: string;
  isDraft?: 'false' | 'true';
  startingTimeFrom?: number;
  startingTimeUntil?: number;
  endingTimeFrom?: number;
  endingTimeUntil?: number;
  channels?: string[];
  startingWeekDays?: string[];
  startTime?: number;
  endTime?: number;
}
export interface EventListRequestParams {
  companyId: string;
  clubId: string;
  params: EventListParams;
}

const getEventList = async ({ companyId, clubId, params }: EventListRequestParams) => {
  const queryParams = paramsSerializer(params as Params);
  const endpoint = `/v1/nightgraph/companies/${companyId}/clubs/${clubId}/events?${queryParams}`;
  const url = `${import.meta.env.VITE_EVENTS_HOST}${endpoint}`;
  return request({ url, method: RequestMethod.GET });
};

export interface GetEventListHookParams {
  view: EventView;
  companyId: string;
  clubId: string;
  infinite?: boolean;
  params: EventListParams;
  options?: Record<string, unknown>;
}

export const useGetEventList = ({ companyId, params, options, clubId, view }: GetEventListHookParams) => {
  const queryParams = [
    `get-event-list-${view}`,
    companyId,
    clubId,
    params.search,
    params.sort,
    params.orderBy,
    params.startingWeekDays,
    params.endingTimeFrom,
    params.endingTimeUntil,
    params.channels,
    params.startTime,
    params.endTime,
    view,
  ];
  const infiniteQuery = useGenericInfiniteQuery<EventListItem[]>(
    queryParams,
    ({ pageParam }) => {
      let offset = 0;
      const initialLimit = params.limit ?? 10;
      if (pageParam !== 0) {
        offset = pageParam === 1 ? initialLimit : (pageParam - 1) * 10 + initialLimit;
      }
      return getEventList({ companyId, clubId, params: { ...params, offset: offset || 0 } });
    },
    {
      ...options,
      getNextPageParam: (lastPage, allPages) => {
        if (
          (allPages.length > 1 && lastPage.length < 10) ||
          (allPages.length === 1 && lastPage.length < (params.limit || 10))
        ) {
          return false;
        }
        return allPages.length;
      },
    },
    false
  );
  return infiniteQuery;
};

export const useGetSimpleEventList = ({ companyId, params, options, clubId, view }: GetEventListHookParams) => {
  const queryParams = [
    `get-event-list-${view}`,
    companyId,
    clubId,
    params.search,
    params.sort,
    params.orderBy,
    params.startingWeekDays,
    params.endingTimeFrom,
    params.endingTimeUntil,
    params.startTime,
    params.endTime,
    view,
  ];
  const query = useGenericQuery<EventListItem[]>(
    queryParams,
    () => getEventList({ companyId, clubId, params }),
    {
      ...options,
    },
    false
  );
  return query;
};
